import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { useAppSelector } from "../store";

interface IToken {
  code: string;
  displayName: string;
  available: number;
  logoUrl?: string;
  memo?: boolean;
  format?: string;
}

export interface BankAccount {
  id: string;
  accountName: string;
  accountNumber: string;
  bankName: string;
  currency: string;
  isDefault: boolean;
}

export type NotabeneStatus =
  | "pending"
  | "authorized"
  | "requires_pii"
  | "rejected"
  | "settled"
  | null;

export interface NotabenePolicy {
  type: string;
  status?: string;
  data?: Record<string, unknown>;
}

export interface WithdrawalsState {
  cryptoWithdrawalStage: "InputDetails" | "TrBackfill" | "Notabene" | "Review" | "Verification" | "Completed";
  fiatWithdrawalStage: "InputDetails" | "Verification" | "Review" | "Completed";
  selectedToken: IToken | null;
  selectedBankAccount: BankAccount | null;
  walletAddress: string;
  displayWalletAddress: string; // Displayed wallet address includes nickname
  walletAddressList: string[];
  memo?: string | undefined;
  withdrawalAmount: number;
  isWithdrawalError: boolean;
  error: string;
  isCryptoWithdrawalModalOpen: boolean;
  isFiatWithdrawalModalOpen: boolean;
  emailCode: string | null;
  code2fa: string | null;
  isEmailCodeSuccess: boolean;
  is2FASuccess: boolean;
  isUnderMaintenance: boolean;
  // Notabene Travel Rule fields
  notabeneTxId: string | null;
  notabeneStatus: NotabeneStatus;
  notabenePolicies: NotabenePolicy[];
  notabeneRequiresPii: boolean;
  // True once the user has clicked "Complete" in the SafeConnect SDK and the
  // resulting IVMS101 has been accepted by our backend. Used to swap the UI
  // from the SDK to the "awaiting counterparty/compliance authorization" view
  // and to resume that view if the modal is closed and reopened mid-flow.
  notabenePiiSubmitted: boolean;
  // Withdrawal-prepared token issued by `POST /withdrawal/prepare` after the
  // user passes email + 2FA at the start of the flow. Required by the
  // Notabene endpoints (in the `X-Withdrawal-Prepared-Token` header) and by
  // the final `processWithdrawal` call (in the body) — without it those
  // endpoints reject the request, so a session-only attacker can't fire the
  // Notabene side-effects before the user has actually authenticated.
  withdrawalPreparedToken: string | null;
  // ISO timestamp when the prepared-withdrawal token expires, so the
  // frontend can surface "your verification expired, please re-enter your
  // codes" if compliance takes too long.
  withdrawalPreparedExpiresAt: string | null;
}

const initialState: WithdrawalsState = {
  cryptoWithdrawalStage: "InputDetails",
  fiatWithdrawalStage: "InputDetails",
  selectedToken: null,
  selectedBankAccount: null,
  walletAddress: "",
  displayWalletAddress: "",
  walletAddressList: [],
  memo: undefined,
  withdrawalAmount: 0,
  isWithdrawalError: false,
  error: "",
  isCryptoWithdrawalModalOpen: false,
  isFiatWithdrawalModalOpen: false,
  emailCode: null,
  code2fa: null,
  isEmailCodeSuccess: false,
  is2FASuccess: false,
  isUnderMaintenance: false,
  // Notabene Travel Rule fields
  notabeneTxId: null,
  notabeneStatus: null,
  notabenePolicies: [],
  notabeneRequiresPii: false,
  notabenePiiSubmitted: false,
  withdrawalPreparedToken: null,
  withdrawalPreparedExpiresAt: null,
};

export const resetWithdrawalThunk = createAsyncThunk(
  "withdrawals/resetWithdrawalThunk",
  async (_, { dispatch }) => {
    dispatch(resetWithdrawalState());
  },
);

export const inputStageThunk = createAsyncThunk(
  "withdrawals/inputStageThunk",
  async (_, { dispatch }) => {
    dispatch(resetInput());
  },
);

const WithdrawalSlice = createSlice({
  name: "withdrawals",
  initialState: initialState,
  reducers: {
    setCryptoWithdrawalStage: (
      state: WithdrawalsState,
      action: PayloadAction<"InputDetails" | "TrBackfill" | "Notabene" | "Review" | "Verification" | "Completed">,
    ): void => {
      state.cryptoWithdrawalStage = action.payload;
    },
    setFiatWithdrawalStage: (
      state: WithdrawalsState,
      action: PayloadAction<"InputDetails" | "Review" | "Verification" | "Completed">,
    ): void => {
      state.fiatWithdrawalStage = action.payload;
    },
    setToken: (state: WithdrawalsState, action: PayloadAction<IToken>): void => {
      state.selectedToken = action.payload;
    },
    setSelectedBankAccount: (state: WithdrawalsState, action: PayloadAction<BankAccount>): void => {
      state.selectedBankAccount = action.payload;
    },
    setWalletAddress: (state: WithdrawalsState, action: PayloadAction<string>): void => {
      state.walletAddress = action.payload;
    },
    setDisplayWalletAddress: (state: WithdrawalsState, action: PayloadAction<string>): void => {
      state.displayWalletAddress = action.payload;
    },
    setWalletAddressList: (state: WithdrawalsState, action: PayloadAction<string[]>): void => {
      state.walletAddressList = action.payload;
    },
    setMemo: (state: WithdrawalsState, action: PayloadAction<string | undefined>): void => {
      state.memo = action.payload;
    },
    setWithdrawalAmount: (state: WithdrawalsState, action: PayloadAction<number>): void => {
      state.withdrawalAmount = action.payload;
    },
    setIsWithdrawalError: (state: WithdrawalsState, action: PayloadAction<boolean>): void => {
      state.isWithdrawalError = action.payload;
    },
    resetWithdrawalState: (state: WithdrawalsState): void => {
      state.isWithdrawalError = false;
      state.error = "";
      state.cryptoWithdrawalStage = "InputDetails";
      state.fiatWithdrawalStage = "InputDetails";
      state.isCryptoWithdrawalModalOpen = false;
      state.isFiatWithdrawalModalOpen = false;
      state.isEmailCodeSuccess = false;
      state.is2FASuccess = false;
      state.emailCode = null;
      state.code2fa = null;
      state.selectedToken = null;
      state.selectedBankAccount = null;
      state.walletAddress = "";
      state.displayWalletAddress = "";
      state.memo = undefined;
      state.withdrawalAmount = 0;
      // Reset Notabene fields
      state.notabeneTxId = null;
      state.notabeneStatus = null;
      state.notabenePolicies = [];
      state.notabeneRequiresPii = false;
      state.notabenePiiSubmitted = false;
      state.withdrawalPreparedToken = null;
      state.withdrawalPreparedExpiresAt = null;
    },
    // Clears just the profile-scoped withdrawal FORM fields (token,
    // destination address, amount) without touching the stage, the
    // open-modal flag, or the notabene fields — for flows that must keep the
    // current screen mounted (the 404-park error in CryptoWithdrawalNotabene)
    // while making sure another profile's destination can't be reused.
    clearWithdrawalFormInput: (state: WithdrawalsState): void => {
      state.selectedToken = null;
      state.walletAddress = "";
      state.displayWalletAddress = "";
      state.memo = undefined;
      state.withdrawalAmount = 0;
    },
    resetInput: (state: WithdrawalsState): void => {
      state.error = "";
      state.emailCode = null;
      state.code2fa = null;
      state.is2FASuccess = false;
      state.isEmailCodeSuccess = false;
      state.cryptoWithdrawalStage = "InputDetails";
      state.fiatWithdrawalStage = "InputDetails";
    },
    setError: (state: WithdrawalsState, action: PayloadAction<string>): void => {
      state.error = action.payload;
    },
    setIsCryptoWithdrawalModalOpen: (
      state: WithdrawalsState,
      action: PayloadAction<boolean>,
    ): void => {
      state.isCryptoWithdrawalModalOpen = action.payload;
    },
    setIsFiatWithdrawalModalOpen: (
      state: WithdrawalsState,
      action: PayloadAction<boolean>,
    ): void => {
      state.isFiatWithdrawalModalOpen = action.payload;
    },
    setEmailCode: (state: WithdrawalsState, action: PayloadAction<string>): void => {
      state.emailCode = action.payload;
    },
    set2FACode: (state: WithdrawalsState, action: PayloadAction<string>): void => {
      state.code2fa = action.payload;
    },
    setIsEmailCodeSuccess: (state: WithdrawalsState, action: PayloadAction<boolean>): void => {
      state.isEmailCodeSuccess = action.payload;
    },
    setIs2FASuccess: (state: WithdrawalsState, action: PayloadAction<boolean>): void => {
      state.is2FASuccess = action.payload;
    },
    setIsUnderMaintenance: (state: WithdrawalsState, action: PayloadAction<boolean>): void => {
      state.isUnderMaintenance = action.payload;
    },
    // Notabene Travel Rule reducers
    setNotabeneTxId: (state: WithdrawalsState, action: PayloadAction<string | null>): void => {
      state.notabeneTxId = action.payload;
    },
    setNotabeneStatus: (state: WithdrawalsState, action: PayloadAction<NotabeneStatus>): void => {
      state.notabeneStatus = action.payload;
    },
    setNotabenePolicies: (
      state: WithdrawalsState,
      action: PayloadAction<NotabenePolicy[]>,
    ): void => {
      state.notabenePolicies = action.payload;
    },
    setNotabeneRequiresPii: (state: WithdrawalsState, action: PayloadAction<boolean>): void => {
      state.notabeneRequiresPii = action.payload;
    },
    setNotabenePiiSubmitted: (state: WithdrawalsState, action: PayloadAction<boolean>): void => {
      state.notabenePiiSubmitted = action.payload;
    },
    setWithdrawalPreparedToken: (
      state: WithdrawalsState,
      action: PayloadAction<{ token: string | null; expiresAt: string | null }>,
    ): void => {
      state.withdrawalPreparedToken = action.payload.token;
      state.withdrawalPreparedExpiresAt = action.payload.expiresAt;
    },
    resetNotabeneState: (state: WithdrawalsState): void => {
      state.notabeneTxId = null;
      state.notabeneStatus = null;
      state.notabenePolicies = [];
      state.notabeneRequiresPii = false;
      state.notabenePiiSubmitted = false;
      state.withdrawalPreparedToken = null;
      state.withdrawalPreparedExpiresAt = null;
    },
  },
});

export const {
  setCryptoWithdrawalStage,
  setFiatWithdrawalStage,
  setToken,
  setSelectedBankAccount,
  setWalletAddress,
  setDisplayWalletAddress,
  setWalletAddressList,
  setMemo,
  setWithdrawalAmount,
  setIsWithdrawalError,
  resetWithdrawalState,
  clearWithdrawalFormInput,
  resetInput,
  setError,
  setIsCryptoWithdrawalModalOpen,
  setIsFiatWithdrawalModalOpen,
  setEmailCode,
  set2FACode,
  setIsEmailCodeSuccess,
  setIs2FASuccess,
  setIsUnderMaintenance,
  // Notabene Travel Rule actions
  setNotabeneTxId,
  setNotabeneStatus,
  setNotabenePolicies,
  setNotabeneRequiresPii,
  setNotabenePiiSubmitted,
  setWithdrawalPreparedToken,
  resetNotabeneState,
} = WithdrawalSlice.actions;

export const useWithdrawalSlice = () => useAppSelector((state) => state.withdrawals);

export default WithdrawalSlice;
